import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

// ─────────────────────────────────────────────────────────────
// Multi-trade sales-rep pricing calculator.
// Base commission = rate × (price per unit − floor) × quantity.
// Add-ons (roofing) are fully commissionable: rep earns rate × add-on total.
// 30% / 40% toggle.
//
// Roofing has TWO material types (Owens Corning, IKO), each with 3 variants.
// Windows has a Patio Doors sub-mode. Insulation has 2 types with explicit targets.
// ─────────────────────────────────────────────────────────────
export const TRADES = {
  roofing: {
    label: 'Roofing', unit: 'square', unitShort: 'sq', sliderMax: 2500,
    materials: {
      'Owens Corning': [
        { label: 'Overlay', floor: 845 },
        { label: 'One Layer Tear-Off', floor: 1025 },
        { label: 'Tear Sheet', floor: 1338 },
      ],
      'IKO': [
        { label: 'Overlay', floor: 735 },
        { label: 'One Layer Tear-Off', floor: 860 },
        { label: 'Tear Sheet', floor: 1200 },
      ],
    },
    // Per-job add-ons, fully commissionable. unit just labels the qty field.
    addons: [
      { key: 'extraLayer', label: 'Extra layer', rate: 35, per: 'sq' },
      { key: 'wood', label: 'Wood', rate: 15, per: 'ft' },
      { key: 'permit', label: 'Permit fee', rate: 800, per: 'flat' },
      { key: 'solar', label: 'Solar panel removal', rate: 250, per: 'panel' },
      { key: 'misc', label: 'Miscellaneous', rate: 1, per: 'dollar' },
    ],
  },
  painting: { label: 'Regular Paint', unit: 'square', unitShort: 'sq', floor: 400, sliderMax: 1500 },
  coating:  {
    label: 'Exterior Coating', unit: 'square', unitShort: 'sq', floor: 575, sliderMax: 2000, celebrateAt: 800,
    // Type picker. The first one is the standard job (same floor as before).
    variants: [
      { label: 'Walls & trim', floor: 575 },
      { label: 'Walls only', floor: 480 },
    ],
  },
  windows: {
    label: 'Windows', unit: 'window', unitShort: 'win', floor: 800, suggested: 1100, sliderMax: 2500,
    // Patio Doors sub-mode: flat base prices the rep sells above
    patio: [
      { label: 'Patio Door 5–8 ft', floor: 2600 },
      { label: 'Patio Door 10–12 ft', floor: 3500 },
    ],
  },
  gutters: { label: 'Gutters', unit: 'linear foot', unitShort: 'ft', floor: 18, sliderMax: 80 },
  cement:  { label: 'Cement', unit: 'square foot', unitShort: 'sqft', floor: 24, sliderMax: 100 },
  insulation: {
    label: 'Insulation', unit: 'square foot', unitShort: 'sqft', sliderMax: 20,
    types: [
      { label: 'Blow-in only', floor: 2, suggested: 4 },
      { label: 'Remove / clean / blow-in', floor: 6, suggested: 9 },
    ],
  },
};
// ─────────────────────────────────────────────────────────────

const fmt = n => '$' + (Math.round(n) || 0).toLocaleString('en-US');
const fmt2 = n => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Remember the calculator per person, per trade ─────────────
// Saved in this browser under the logged-in user's name, so going back, refreshing or
// closing the browser keeps the numbers. "Clear" wipes them for a new job.
const STORE = 'cpr-comm-calc';
const storeKey = (user, trade) => `${STORE}:${user || 'me'}:${trade}`;
function loadSaved(key) {
  try { const v = JSON.parse(window.localStorage.getItem(key) || 'null'); return v && typeof v === 'object' ? v : null; }
  catch { return null; }
}

// A fresh calculator for each trade (so each trade restores its own saved numbers)
export default function TradeCalculator(props) {
  return <TradeCalculatorInner key={props.trade || 'roofing'} {...props} />;
}

function TradeCalculatorInner({ trade = 'roofing' }) {
  const cfg = TRADES[trade] || TRADES.roofing;
  const auth = useAuth();
  const KEY = storeKey(String(auth?.username || auth?.staff_name || '').trim().toLowerCase(), trade);
  const [saved] = useState(() => loadSaved(KEY));   // read once when the calculator opens

  // ── Roofing material + variant ─────────────────────────────
  const hasMaterials = !!cfg.materials;
  const materialNames = hasMaterials ? Object.keys(cfg.materials) : [];
  const [material, setMaterial] = useState(materialNames.includes(saved?.material) ? saved.material : (materialNames[0] || ''));
  const variantList = hasMaterials ? (cfg.materials[material] || []) : (cfg.variants || cfg.patio || cfg.types || []);
  const hasVariants = variantList.length > 0;
  const [variantIdx, setVariantIdx] = useState(() => {
    const i = Number(saved?.variantIdx);
    const list = hasMaterials ? (cfg.materials[material] || []) : saved?.patioMode ? (cfg.patio || []) : (cfg.variants || cfg.types || []);
    return Number.isInteger(i) && i >= 0 && i < Math.max(list.length, 1) ? i : 0;
  });

  // ── Windows patio-door sub-mode ────────────────────────────
  const hasPatio = !!cfg.patio;
  const [patioMode, setPatioMode] = useState(!!(hasPatio && saved?.patioMode));

  // ── Insulation types ───────────────────────────────────────
  const hasTypes = !!cfg.types;

  // Resolve the active floor + suggested target
  let FLOOR, SUGGESTED;
  if (hasMaterials) {
    FLOOR = (cfg.materials[material]?.[variantIdx]?.floor) ?? 845;
    SUGGESTED = FLOOR * 1.5;
  } else if (hasTypes) {
    const t = cfg.types[Math.min(variantIdx, cfg.types.length - 1)];
    FLOOR = t.floor; SUGGESTED = t.suggested;
  } else if (cfg.variants) {
    FLOOR = (cfg.variants[Math.min(variantIdx, cfg.variants.length - 1)]?.floor) ?? cfg.floor;
    SUGGESTED = FLOOR * 1.5;
  } else if (hasPatio && patioMode) {
    FLOOR = cfg.patio[Math.min(variantIdx, cfg.patio.length - 1)].floor;
    SUGGESTED = FLOOR * 1.25;
  } else {
    FLOOR = cfg.floor;
    SUGGESTED = cfg.suggested || FLOOR * 1.5;
  }

  const SLIDER_MAX = (hasPatio && patioMode) ? FLOOR * 2 : (cfg.sliderMax || FLOOR * 4);
  const CELEBRATE_AT = cfg.celebrateAt || Math.round(SUGGESTED);
  const stepGuess = FLOOR >= 1000 ? 50 : FLOOR >= 100 ? 5 : 1;
  const unitShort = (hasPatio && patioMode) ? 'door' : cfg.unitShort;
  const unitWord = (hasPatio && patioMode) ? 'door' : cfg.unit;

  const okNum = v => Number.isFinite(Number(v)) && v !== null && v !== '';
  const [qty, setQty] = useState(okNum(saved?.qty) ? Number(saved.qty) : 10);
  const [pricePerUnit, setPricePerUnit] = useState(okNum(saved?.pricePerUnit) ? Number(saved.pricePerUnit) : Math.round(SUGGESTED));
  const [rate, setRate] = useState(okNum(saved?.rate) ? Number(saved.rate) : 0.40);
  const [celebrate, setCelebrate] = useState(false);
  const [addonVals, setAddonVals] = useState(saved?.addonVals && typeof saved.addonVals === 'object' ? saved.addonVals : {});
  const prev = useRef(pricePerUnit);
  const opened = useRef(false);   // skip the "reset price" on first open, so saved numbers stay

  // Reset price when the effective floor changes (not when the calculator first opens)
  useEffect(() => {
    if (!opened.current) { opened.current = true; return; }
    setPricePerUnit(Math.round(SUGGESTED));
    prev.current = Math.round(SUGGESTED);
  }, [material, variantIdx, patioMode]); // eslint-disable-line

  // Save every change
  useEffect(() => {
    try { window.localStorage.setItem(KEY, JSON.stringify({ material, variantIdx, patioMode, qty, pricePerUnit, rate, addonVals })); } catch { /* storage blocked: nothing saved */ }
  }, [KEY, material, variantIdx, patioMode, qty, pricePerUnit, rate, addonVals]);

  function clearAll() {
    try { window.localStorage.removeItem(KEY); } catch { /* ignore */ }
    const m = materialNames[0] || '';
    const sameFloor = material === m && variantIdx === 0 && !patioMode;
    setMaterial(m); setVariantIdx(0); setPatioMode(false); setAddonVals({}); setQty(10); setRate(0.40);
    // when the type doesn't change, the price-reset above won't run, so reset it here
    if (sameFloor) { setPricePerUnit(Math.round(SUGGESTED)); prev.current = Math.round(SUGGESTED); }
  }

  useEffect(() => {
    if (prev.current < CELEBRATE_AT && pricePerUnit >= CELEBRATE_AT) {
      setCelebrate(true); const t = setTimeout(() => setCelebrate(false), 1600); prev.current = pricePerUnit; return () => clearTimeout(t);
    }
    prev.current = pricePerUnit;
  }, [pricePerUnit, CELEBRATE_AT]);

  const spread = Math.max(pricePerUnit - FLOOR, 0);
  const totalSpread = spread * qty;
  const baseJob = pricePerUnit * qty;

  // Add-on totals (roofing). misc is a raw dollar amount; others are rate × qty.
  const addonList = cfg.addons || [];
  let addonTotal = 0;
  const addonBreakdown = addonList.map(a => {
    const v = parseFloat(addonVals[a.key]) || 0;
    const amount = a.per === 'flat' ? (addonVals[a.key] ? a.rate : 0)
                 : a.per === 'dollar' ? v
                 : a.rate * v;
    addonTotal += amount;
    return { ...a, input: v, amount };
  });

  const totalJob = baseJob + addonTotal;
  const baseCommission = totalSpread * rate;
  const addonCommission = addonTotal * rate;   // add-ons fully commissionable
  const commission = baseCommission + addonCommission;

  const aboveCeleb = pricePerUnit >= CELEBRATE_AT;
  const atTarget = pricePerUnit >= SUGGESTED;
  const targetComm = (SUGGESTED - FLOOR) * qty * rate;
  const gap = Math.max(targetComm - baseCommission, 0);

  const rating = (() => {
    if (pricePerUnit < FLOOR + stepGuess) return { label: 'AT FLOOR', color: '#c62828' };
    if (pricePerUnit < SUGGESTED) return { label: 'BELOW TARGET', color: '#E65100' };
    if (pricePerUnit < CELEBRATE_AT) return { label: 'GOOD', color: '#1B5E20' };
    if (pricePerUnit < SLIDER_MAX * 0.7) return { label: '🔥 CRUSHING IT', color: '#0D3B2E' };
    return { label: '🚀 PREMIUM', color: '#0D3B2E' };
  })();

  const sliderPos = Math.min(pricePerUnit, SLIDER_MAX);
  const pct = v => ((v - FLOOR) / (SLIDER_MAX - FLOOR)) * 100;
  const ticks = [FLOOR, Math.round(FLOOR + (SLIDER_MAX - FLOOR) / 3), Math.round(FLOOR + 2 * (SLIDER_MAX - FLOOR) / 3), SLIDER_MAX];

  // ── Dark / gold theme ──
  const GOLD = '#f0c842';
  const GOLD_DIM = '#c9a227';
  const card = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(240,200,66,0.25)', borderRadius: 12, padding: 16 };
  const selStyle = { padding: '7px 11px', border: '1px solid ' + GOLD_DIM, borderRadius: 8, fontSize: 13, fontWeight: 600, color: GOLD, background: '#1a1a1a' };

  return (
    <div className="cpr-calc-dark" style={{ maxWidth: 760, position: 'relative', background: 'linear-gradient(160deg,#1a1a1a 0%,#0d0d0d 60%,#000 100%)', borderRadius: 16, padding: 22, boxShadow: '0 8px 30px rgba(0,0,0,0.4)' }}>
      <style>{`
        .cpr-calc-dark, .cpr-calc-dark * { color: #f2f2f2; }
        .cpr-calc-dark .cl-gold, .cpr-calc-dark .cl-gold * { color: #f0c842 !important; }
        .cpr-calc-dark .cl-muted, .cpr-calc-dark .cl-muted * { color: #9a9a9a !important; }
        .cpr-calc-dark input[type=number], .cpr-calc-dark input[type=text] { background:#1a1a1a !important; color:#f2f2f2 !important; }
        .cpr-calc-dark select { color:#f0c842 !important; }
        .cpr-calc-dark select option { background:#1a1a1a; color:#f2f2f2; }
        .cpr-calc-dark input[type=range] { accent-color:#f0c842 !important; }
        .cpr-calc-dark .cpr-darktext, .cpr-calc-dark .cpr-darktext * { color:#1a1a1a !important; }
        @keyframes cprFloat { 0% { transform: translateY(0); opacity: 0; } 30% { opacity: 1; } 100% { transform: translateY(-60px); opacity: 0; } }
        @keyframes cprPulse { 0% { transform: scale(1); } 40% { transform: scale(1.18); } 100% { transform: scale(1); } }
        .cpr-celebrate { animation: cprPulse 0.7s ease-out; }
      `}</style>

      {celebrate && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 0, zIndex: 20, pointerEvents: 'none' }}>
          {[12, 28, 44, 60, 76, 88].map((left, i) => (
            <span key={i} style={{ position: 'absolute', left: `${left}%`, top: 120, fontSize: 26 + (i % 3) * 8, animation: `cprFloat 1.5s ease-out ${i * 0.08}s both` }}>💲</span>
          ))}
        </div>
      )}

      {/* Selectors */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <button type="button" onClick={clearAll} title="Start a new calculation"
          style={{ order: 99, marginLeft: 'auto', padding: '7px 12px', border: '1px solid ' + GOLD_DIM, borderRadius: 8, fontSize: 12, fontWeight: 700, color: GOLD, background: 'transparent', cursor: 'pointer' }}>
          ↺ Clear
        </button>
        {hasMaterials && (
          <div>
            <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: .5, marginRight: 6 }}>Material:</label>
            <select value={material} onChange={e => { setMaterial(e.target.value); setVariantIdx(0); }} style={selStyle}>
              {materialNames.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )}
        {hasVariants && !hasTypes && !(hasPatio && !patioMode) && (
          <div>
            <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: .5, marginRight: 6 }}>Type:</label>
            <select value={variantIdx} onChange={e => setVariantIdx(parseInt(e.target.value))} style={selStyle}>
              {variantList.map((v, i) => <option key={i} value={i}>{v.label} — floor {fmt(v.floor)}/{unitShort}</option>)}
            </select>
          </div>
        )}
        {hasTypes && (
          <div>
            <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: .5, marginRight: 6 }}>Type:</label>
            <select value={variantIdx} onChange={e => setVariantIdx(parseInt(e.target.value))} style={selStyle}>
              {cfg.types.map((t, i) => <option key={i} value={i}>{t.label} — base {fmt(t.floor)}, target {fmt(t.suggested)}</option>)}
            </select>
          </div>
        )}
        {hasPatio && (
          <div>
            <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: .5, marginRight: 6 }}>Mode:</label>
            <select value={patioMode ? 'patio' : 'window'} onChange={e => { setPatioMode(e.target.value === 'patio'); setVariantIdx(0); }} style={selStyle}>
              <option value="window">Windows — floor {fmt(cfg.floor)}/win</option>
              <option value="patio">Patio Doors</option>
            </select>
            {patioMode && (
              <select value={variantIdx} onChange={e => setVariantIdx(parseInt(e.target.value))} style={{ ...selStyle, marginLeft: 8 }}>
                {cfg.patio.map((p, i) => <option key={i} value={i}>{p.label} — base {fmt(p.floor)}</option>)}
              </select>
            )}
          </div>
        )}
      </div>

      <div style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>
        {cfg.label}{hasMaterials ? ` · ${material}` : ''} — priced per {unitWord}. Target: <strong>{fmt2(SUGGESTED)}/{unitShort}</strong> (floor {fmt(FLOOR)}).
      </div>

      {/* Quantity + Price */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 18 }}>
        <div style={card}>
          <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Quantity ({unitWord}s)</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button type="button" onClick={() => setQty(q => Math.max(1, q - 1))} style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid #c9a227', background: '#2a2a2a', fontSize: 18, cursor: 'pointer' }}>−</button>
            <input type="number" min={1} value={qty} onChange={e => setQty(Math.max(1, parseInt(e.target.value) || 1))}
              className="cl-gold"
              style={{ width: 70, fontSize: 26, fontWeight: 700, textAlign: 'center', border: 'none', borderBottom: '2px solid #f0c842', outline: 'none', background: 'transparent' }} />
            <button type="button" onClick={() => setQty(q => q + 1)} style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid #c9a227', background: '#2a2a2a', fontSize: 18, cursor: 'pointer' }}>+</button>
          </div>
        </div>

        <div style={card}>
          <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
            <span>Price Per {unitShort}</span><span style={{ color: '#bbb', textTransform: 'none' }}>type any amount</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="cl-gold" style={{ fontSize: 26, fontWeight: 700 }}>$</span>
            <input type="number" min={FLOOR} value={pricePerUnit} onChange={e => setPricePerUnit(Math.max(0, parseInt(e.target.value) || 0))}
              className="cl-gold"
              style={{ width: 130, fontSize: 26, fontWeight: 700, border: 'none', borderBottom: '2px solid #f0c842', outline: 'none', background: 'transparent' }} />
          </div>
          <input type="range" min={FLOOR} max={SLIDER_MAX} step={stepGuess} value={sliderPos} onChange={e => setPricePerUnit(parseInt(e.target.value))}
            style={{ width: '100%', marginTop: 12, accentColor: rating.color }} />
          <div style={{ position: 'relative', height: 16, fontSize: 9, color: '#999', marginTop: 2 }}>
            {ticks.map((v, i, a) => (
              <div key={v} style={{ position: 'absolute', left: `${pct(v)}%`, transform: i === 0 ? 'translateX(0)' : i === a.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(v)}</div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 14, fontSize: 10, color: '#999', marginTop: 6, flexWrap: 'wrap' }}>
            <span><span style={{ color: '#1B5E20', fontWeight: 700 }}>●</span> Target {fmt(SUGGESTED)}</span>
          </div>
        </div>
      </div>

      {/* Roofing add-ons */}
      {addonList.length > 0 && (
        <div style={{ ...card, marginBottom: 18 }}>
          <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Add-ons (added to job &amp; fully commissionable)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {addonList.map(a => (
              <div key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, flex: 1 }}>{a.label}
                  <span style={{ color: '#aaa', fontSize: 11 }}>
                    {a.per === 'flat' ? ` (${fmt(a.rate)} flat)` : a.per === 'dollar' ? ' ($ amount)' : ` (${fmt(a.rate)}/${a.per})`}
                  </span>
                </span>
                {a.per === 'flat' ? (
                  <input type="checkbox" checked={!!addonVals[a.key]} onChange={e => setAddonVals(s => ({ ...s, [a.key]: e.target.checked }))} style={{ width: 18, height: 18 }} />
                ) : (
                  <input type="number" min={0} placeholder="0" value={addonVals[a.key] || ''} onChange={e => setAddonVals(s => ({ ...s, [a.key]: e.target.value }))}
                    style={{ width: 80, padding: '5px 8px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13 }} />
                )}
              </div>
            ))}
          </div>
          {addonTotal > 0 && <div style={{ marginTop: 10, fontSize: 13, color: '#1B5E20', fontWeight: 600 }}>Add-ons total: {fmt2(addonTotal)} (+{fmt2(addonCommission)} commission)</div>}
        </div>
      )}

      {/* Rate toggle + rating */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'inline-flex', border: '1px solid #c9a227', borderRadius: 8, overflow: 'hidden' }}>
          {[['30%', 0.30], ['40%', 0.40]].map(([l, v]) => (
            <button key={l} type="button" onClick={() => setRate(v)} className={rate === v ? 'cpr-darktext' : ''} style={{ padding: '8px 22px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14, background: rate === v ? '#f0c842' : 'transparent', color: rate === v ? '#1a1a1a' : '#f0c842' }}>{l}</button>
          ))}
        </div>
        <span style={{ background: rating.color, fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 20, letterSpacing: 1, transform: celebrate ? 'scale(1.08)' : 'scale(1)', color: '#fff' }}>{rating.label}</span>
      </div>

      {/* Totals */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 18 }}>
        <div style={{ ...card, textAlign: 'center' }}>
          <div className="cl-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Total Job Value</div>
          <div className="cl-gold" style={{ fontSize: 30, fontWeight: 700 }}>{fmt(totalJob)}</div>
          <div className="cl-muted" style={{ fontSize: 11, marginTop: 4 }}>{qty} {unitShort} × {fmt(pricePerUnit)}{addonTotal > 0 ? ` + ${fmt(addonTotal)} add-ons` : ''}</div>
        </div>
        <div style={{ background: aboveCeleb ? 'rgba(240,200,66,0.16)' : 'rgba(240,200,66,0.08)', border: '2px solid #f0c842', borderRadius: 12, padding: 18, textAlign: 'center' }}>
          <div className="cl-gold" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Your Commission</div>
          <div className={'cl-gold ' + (celebrate ? 'cpr-celebrate' : '')} style={{ display: 'inline-block', fontSize: 32, fontWeight: 700 }}>{fmt2(commission)}</div>
          <div className="cl-muted" style={{ fontSize: 11, marginTop: 4 }}>{(rate * 100)}% rate</div>
        </div>
      </div>

      {!atTarget && gap > 0 && (
        <div style={{ background: 'rgba(240,200,66,0.10)', border: '1px solid #c9a227', borderRadius: 12, padding: 16, marginBottom: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Push to the {fmt2(SUGGESTED)}/{unitShort} target</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>+{fmt2(gap)} more in your pocket</div>
        </div>
      )}

      {/* Breakdown */}
      <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(240,200,66,0.2)', borderRadius: 12, padding: 18 }}>
        <div className="cl-gold" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>How your commission is calculated</div>
        {[
          [`Price per ${unitShort}`, fmt(pricePerUnit), '#333'],
          ['Base floor', '− ' + fmt(FLOOR), '#c62828'],
          [`Spread per ${unitShort}`, '= ' + fmt(spread), '#333'],
          ['× Quantity', '× ' + qty, '#333'],
          ['Base commission', '= ' + fmt2(baseCommission) + ' (×' + (rate * 100) + '%)', '#333'],
        ].map(([l, v, c], i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, color: '#555' }}>
            <span>{l}</span><span style={{ color: c, fontWeight: 600 }}>{v}</span>
          </div>
        ))}
        {addonTotal > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, color: '#555' }}>
            <span>Add-on commission</span><span style={{ color: '#333', fontWeight: 600 }}>+ {fmt2(addonCommission)}</span>
          </div>
        )}
        <div className="cl-gold" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', marginTop: 6, borderTop: '1px solid rgba(240,200,66,0.3)', fontSize: 15, fontWeight: 700 }}>
          <span>Your commission</span><span>{fmt2(commission)}</span>
        </div>
      </div>

      <div style={{ textAlign: 'center', fontSize: 10, color: '#bbb', marginTop: 14, letterSpacing: 1, textTransform: 'uppercase' }}>Internal use only — do not share with customers</div>
    </div>
  );
}
